import { looksLikeIsbn, normalizeIsbn } from '@bookclub/shared';
import { router, useLocalSearchParams } from 'expo-router';
import { ScanBarcode } from 'lucide-react-native';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Platform, Text, View } from 'react-native';
import { lookupIsbn, useBookSearch } from '@/api/books';
import { useClub } from '@/api/clubs';
import { ApiError } from '@/api/client';
import { useReadings, useWantToRead } from '@/api/readings';
import { bookErrorMessage } from '@/books/errors';
import { parsePick } from '@/books/pick';
import { formatAuthors } from '@/books/format';
import { BookRow } from '@/components/BookRow';
import { PageTitle } from '@/components/PageTitle';
import { CoverRow, PopularBooks } from '@/components/PopularBooks';
import { Screen } from '@/components/Screen';
import { Button } from '@/components/ui/Button';
import { Notice } from '@/components/ui/Notice';
import { Hint } from '@/components/ui/Section';
import { TextField } from '@/components/ui/TextField';
import { TextLink } from '@/components/ui/TextLink';
import { useTheme } from '@/theme';

const DEBOUNCE_MS = 450;

export default function FindBook() {
  const { colors, fonts, fontSize, space } = useTheme();
  const { t } = useTranslation();
  const params = useLocalSearchParams<{ q?: string; pick?: string }>();
  // Picking the book for a club: every link carries the club id along.
  const pick = params.pick ? { pick: params.pick } : {};
  const picking = parsePick(params.pick);
  const pickingFor = useClub(picking?.clubId ?? '', { enabled: Boolean(picking?.clubId) });
  const [text, setText] = useState(params.q ?? '');
  const [query, setQuery] = useState(params.q ?? '');
  const [isbnState, setIsbnState] = useState<{ busy: boolean; error?: string; notFound?: string }>({ busy: false });

  // Titles search as you type (after a pause); ISBNs are looked up on Enter.
  useEffect(() => {
    if (looksLikeIsbn(text)) return;
    const timer = setTimeout(() => setQuery(text), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const search = useBookSearch(query);
  const readings = useReadings();
  const want = useWantToRead();
  // Your own relation to each book in the results: reading it beats having read it beats wanting it.
  const status = useMemo(() => {
    const byBook = new Map<string, string>();
    for (const b of want.data ?? []) byBook.set(b.bookKey, t('books.status.want'));
    for (const r of readings.data ?? []) if (r.status === 'finished') byBook.set(r.bookKey, t('books.status.finished'));
    for (const r of readings.data ?? []) if (r.status === 'reading') byBook.set(r.bookKey, t('books.status.reading'));
    return byBook;
  }, [want.data, readings.data, t]);
  // Before anything is typed: books you saved for later, and what people are reading this week.
  const idle = text.trim().length < 2 && !isbnState.busy && !isbnState.notFound && !isbnState.error;

  async function submit() {
    if (!looksLikeIsbn(text)) {
      setQuery(text);
      return;
    }
    const isbn = normalizeIsbn(text);
    if (!isbn) {
      setIsbnState({ busy: false, error: t('books.isbnInvalid') });
      return;
    }
    setIsbnState({ busy: true });
    try {
      const edition = await lookupIsbn(isbn);
      setIsbnState({ busy: false });
      router.push({ pathname: '/books/edition/[id]', params: { id: edition.id, ...pick } });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setIsbnState({ busy: false, notFound: isbn });
      else setIsbnState({ busy: false, error: bookErrorMessage(t, err) });
    }
  }

  const works = search.data?.works ?? [];
  const showEmpty = search.isSuccess && works.length === 0 && query.trim().length >= 2;

  return (
    <Screen>
      <PageTitle title={t('books.title')} />
      <Text accessibilityRole="header" style={{ fontFamily: fonts.headingBold, fontSize: fontSize.xxl, color: colors.text }}>
        {t('books.title')}
      </Text>
      {picking?.kind === 'club' && pickingFor.data && <Hint>{t('books.pickingFor', { club: pickingFor.data.name })}</Hint>}
      {picking?.kind === 'read' && <Hint>{t('books.pickingForReading')}</Hint>}
      <View style={{ marginTop: space.xl, gap: space.md }}>
        <TextField
          label={t('books.searchLabel')}
          hint={t('books.searchHint')}
          value={text}
          onChangeText={(value) => {
            setText(value);
            setIsbnState({ busy: false });
          }}
          // On the web the keyboard is already there; on phones it would cover Scan and the suggestions.
          autoFocus={Platform.OS === 'web'}
          autoCorrect={false}
          returnKeyType="search"
          inputMode="search"
          onSubmitEditing={submit}
        />
        {isbnState.error && <Notice message={isbnState.error} />}
        {isbnState.notFound && (
          <View style={{ gap: space.xs }}>
            <Notice message={t('books.isbnNotFound', { isbn: isbnState.notFound })} />
            <TextLink href={{ pathname: '/books/new', params: { isbn: isbnState.notFound, ...pick } }} label={t('books.addManually')} />
          </View>
        )}
        {search.isError && <Notice message={bookErrorMessage(t, search.error)} />}
        {Platform.OS !== 'web' && (
          <Button
            variant="secondary"
            label={t('books.scan')}
            icon={<ScanBarcode size={20} color={colors.text} strokeWidth={1.75} />}
            onPress={() => router.push({ pathname: '/books/scan', params: pick })}
          />
        )}
      </View>

      {idle && (
        <View style={{ marginTop: space.xl, gap: space.xl }}>
          <CoverRow
            title={t('books.fromWant')}
            items={(want.data ?? []).map((b) => ({
              key: b.id,
              title: b.edition.title,
              authors: b.edition.authors,
              cover: b.edition.cover,
              href: b.edition.workKey
                ? { pathname: '/books/work/[key]', params: { key: b.edition.workKey, ...pick } }
                : { pathname: '/books/edition/[id]', params: { id: b.edition.id, ...pick } },
            }))}
          />
          <PopularBooks pick={params.pick} />
        </View>
      )}

      <View style={{ marginTop: space.lg, gap: space.xs }} accessibilityLiveRegion="polite">
        {(search.isFetching || isbnState.busy) && (
          <View style={{ flexDirection: 'row', gap: space.sm, alignItems: 'center', paddingVertical: space.sm }}>
            <ActivityIndicator color={colors.accent} />
            <Hint>{t('books.searching')}</Hint>
          </View>
        )}
        {showEmpty && <Hint>{t('books.noResults', { query: query.trim() })}</Hint>}
        {works.map((work) => (
          // A translation you searched for shows as itself, with the book it belongs to underneath.
          <BookRow
            key={work.key}
            href={{ pathname: '/books/work/[key]', params: { key: work.key, ...(work.match ? { edition: work.match.key } : {}), ...pick } }}
            cover={work.match?.cover ?? work.cover}
            title={work.match?.title ?? work.title}
            note={status.get(`w:${work.key}`)}
            lines={[
              formatAuthors(work.authors),
              [
                work.match ? work.title : null,
                work.firstPublished ? t('books.firstPublished', { year: work.firstPublished }) : null,
                t('books.editions', { count: work.editionCount }),
              ]
                .filter(Boolean)
                .join(' · '),
            ]}
          />
        ))}
      </View>

      <View style={{ marginTop: space.xl, gap: space.xs }}>
        <Hint>{t('books.cantFind')}</Hint>
        <TextLink href={{ pathname: '/books/new', params: pick }} label={t('books.addManually')} />
      </View>
    </Screen>
  );
}
